import { useState } from "react";
import { createPortal } from "react-dom";

const RANDOM_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
function randomPassword(length = 10) {
  let pw = "";
  for (let i = 0; i < length; i++) pw += RANDOM_CHARS[Math.floor(Math.random() * RANDOM_CHARS.length)];
  return pw;
}

/**
 * Admin "reset password" modal — shared by AdminUsers.js (customer/business
 * accounts) and RiderApproval.js (dispatchers), since both need the exact
 * same flow: pick or generate a new password, submit, show it once so the
 * admin can relay it to the account holder.
 *
 * Portaled to <body> — .page has backdrop-filter, which creates a
 * containing block for position:fixed descendants, so a nested fixed modal
 * would center on the scrollable page instead of the viewport.
 *
 * Props:
 *  - name       (string)          — account holder's name, shown in the title
 *  - onSubmit   (password) => Promise  — calls the actual reset API
 *  - onClose    () => void
 */
export default function ResetPasswordModal({ name, onSubmit, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async () => {
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await onSubmit(newPassword);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || "Could not reset password.");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(newPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard access denied — the field is still selectable manually */ }
  };

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px", boxSizing: "border-box" }}>
      <div className="delivery-card" style={{ maxWidth: "420px", width: "100%", margin: 0, maxHeight: "calc(100vh - 40px)", overflowY: "auto", WebkitOverflowScrolling: "touch", boxSizing: "border-box" }}>
        <h3 style={{ marginBottom: "16px" }}>Reset Password — {name}</h3>

        {done ? (
          <>
            <div className="goboot-ai-banner" style={{ marginBottom: "16px" }}>
              <div className="ai-banner-icon">✅</div>
              <div className="ai-banner-text">
                <strong>Password reset.</strong> Share it with the account holder securely — it won't be shown again.
              </div>
            </div>
            <div style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", padding: "12px 16px", fontFamily: "monospace", fontSize: "14px", wordBreak: "break-all", marginBottom: "16px", color: "var(--success-hover)" }}>
              {newPassword}
            </div>
            <button onClick={handleCopy} style={{ width: "100%", marginBottom: "8px" }}>
              {copied ? "✅ Copied!" : "📋 Copy Password"}
            </button>
            <button className="secondary" style={{ width: "100%" }} onClick={onClose}>Close</button>
          </>
        ) : (
          <>
            <p className="hint" style={{ marginBottom: "12px" }}>
              Set a new password for this account. They'll need it the next time they log in — this doesn't
              notify them automatically, so relay it yourself.
            </p>
            <div className="info-row" style={{ flexDirection: "column", alignItems: "flex-start" }}>
              <label style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>New Password</label>
              <div style={{ display: "flex", gap: "8px", width: "100%" }}>
                <input
                  type="text"
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  style={{ flex: 1, margin: 0 }}
                />
                <button
                  type="button"
                  className="secondary"
                  style={{ width: "auto", margin: 0, padding: "10px 14px" }}
                  onClick={() => setNewPassword(randomPassword())}
                >
                  🎲 Generate
                </button>
              </div>
            </div>

            {error && <p className="error" style={{ marginTop: "8px" }}>{error}</p>}

            <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
              <button className="secondary" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
              <button style={{ flex: 1 }} disabled={busy} onClick={handleSubmit}>
                {busy ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
